import { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Archive, FileText, X, MoreVertical, Edit, ChevronDown, ChevronUp, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  QUIET_MENU_DESTRUCTIVE_ITEM_CLASS,
  QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS,
} from '@/lib/menu-chrome';
import { getConversations, deleteConversation } from '@/lib/api';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConversationRenameDialog } from './conversation-rename-dialog';

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  archived_at?: string;
  message_count: number;
  archived?: boolean;
  last_message_role?: 'user' | 'assistant';
  preview?: string;
}

interface ActiveConversation {
  id: string;
  title: string;
  message_count?: number;
  updated_at?: string;
  created_at?: string;
}

interface ConversationHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectConversation: (id: string) => void;
  onArchiveConversation?: (id: string) => void | Promise<void>;
  onDeleteConversation?: (id: string) => void;
  onRenameConversation?: (id: string, newTitle: string) => void;
  onNewChat?: () => void;
  conversations?: Conversation[];
  activeConversations?: ActiveConversation[];
  currentConversationId?: string;
  refreshTrigger?: number; // Trigger refresh without remounting
}

// Mock data for prototype - More realistic and complete history
const MOCK_CONVERSATIONS: Conversation[] = [
  // Today's conversations
  {
    id: '1',
    title: 'Review recent UX improvements',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    message_count: 12,
    preview: 'Can you help me review the recent UX improvements we made to the sidebar?'
  },
  {
    id: '2',
    title: 'Check status of phase 7 work',
    created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    message_count: 8,
    preview: 'What\'s the current status of the conversation history UI implementation?'
  },
  {
    id: '3',
    title: 'Sent to Chat',
    created_at: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(), // 7 hours ago
    updated_at: new Date(Date.now() - 7 * 60 * 60 * 1000).toISOString(),
    message_count: 5,
    preview: 'I need help with implementing the scroll-based header transitions'
  },
  {
    id: '4',
    title: 'Review phase 0 day plan and progress',
    created_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(), // 12 hours ago
    updated_at: new Date(Date.now() - 12 * 60 * 60 * 1000).toISOString(),
    message_count: 15,
    preview: 'Can you review the phase 0 day plan and provide an update on our progress?'
  },
  {
    id: '5',
    title: 'Mimic collapsible sidebar widget pattern',
    created_at: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(), // 16 hours ago
    updated_at: new Date(Date.now() - 16 * 60 * 60 * 1000).toISOString(),
    message_count: 9,
    preview: 'How can I implement a collapsible sidebar widget similar to VS Code?'
  },

  // Yesterday's conversations
  {
    id: '6',
    title: 'New Chat',
    created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Yesterday
    updated_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    message_count: 3,
    preview: 'Hello! I need help with my FlowNotes project'
  },
  {
    id: '7',
    title: 'Improve search filters and error handling',
    created_at: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 25 * 60 * 60 * 1000).toISOString(),
    message_count: 11,
    preview: 'The search functionality needs better filters and error handling'
  },
  {
    id: '8',
    title: 'Remove TipTap debugging logs',
    created_at: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 26 * 60 * 60 * 1000).toISOString(),
    message_count: 4,
    preview: 'Can you help me remove the debugging logs from the TipTap editor?'
  },
  {
    id: '9',
    title: 'Typo or incomplete message',
    created_at: new Date(Date.now() - 27 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 27 * 60 * 60 * 1000).toISOString(),
    message_count: 2,
    preview: 'I think there might be a typo in the error message'
  },
  {
    id: '10',
    title: 'Scrolling bug in WYSIWYG editor',
    created_at: new Date(Date.now() - 28 * 60 * 60 * 1000).toISOString(),
    updated_at: new Date(Date.now() - 28 * 60 * 60 * 1000).toISOString(),
    message_count: 7,
    preview: 'There\'s a scrolling issue in the WYSIWYG editor when content is long'
  },

  // This week's conversations
  {
    id: '11',
    title: 'Review documentation issue in repository',
    created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago
    updated_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 6,
    preview: 'There seems to be an issue with the documentation in the repository'
  },
  {
    id: '12',
    title: 'Debug WYSIWYG editor scrolling issue',
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 13,
    preview: 'The WYSIWYG editor has a scrolling bug that needs to be fixed'
  },
  {
    id: '13',
    title: 'Projects for the greater good this week',
    created_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(), // 4 days ago
    updated_at: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 9,
    preview: 'What projects should I focus on this week for the greater good?'
  },
  {
    id: '14',
    title: 'Improve UX for flownotes app',
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
    updated_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 18,
    preview: 'How can I improve the user experience for the FlowNotes application?'
  },
  {
    id: '15',
    title: 'Discussing flownotes integrations and MCP advantages',
    created_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(), // 6 days ago
    updated_at: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 22,
    preview: 'Let\'s discuss the advantages of MCP integrations for FlowNotes'
  },

  // Older conversations
  {
    id: '16',
    title: 'Database migration planning',
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week ago
    updated_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 15,
    preview: 'Help me plan the database migration strategy for the new conversation tables'
  },
  {
    id: '17',
    title: 'Code review feedback',
    created_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(), // 8 days ago
    updated_at: new Date(Date.now() - 8 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 6,
    preview: 'Can you review this PR and provide feedback on the implementation?'
  },
  {
    id: '18',
    title: 'Review frontend implementation and testing advice',
    created_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago
    updated_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 14,
    preview: 'I need advice on frontend implementation and testing strategies'
  },
  {
    id: '19',
    title: 'Voice memo processing workflow',
    created_at: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(), // 12 days ago
    updated_at: new Date(Date.now() - 12 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 20,
    preview: 'How can I improve the voice memo processing workflow?'
  },
  {
    id: '20',
    title: 'Authentication system design',
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
    updated_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    message_count: 25,
    preview: 'Let\'s design a robust authentication system for FlowNotes'
  },
];

function organizeConversations(conversations: Conversation[]) {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  
  // Separate active and archived
  const active = conversations.filter(c => !c.archived);
  const archived = conversations.filter(c => c.archived);
  
  // RECENT: Updated in last 7 days
  const recent = active.filter(c => 
    new Date(c.updated_at) >= sevenDaysAgo
  );
  
  // OLDER: Older than 7 days but not archived
  const older = active.filter(c => 
    new Date(c.updated_at) < sevenDaysAgo
  );
  
  return {
    recent,
    older,
    archived
  };
}

export function ConversationHistoryPanel({
  isOpen,
  onClose,
  onSelectConversation,
  onArchiveConversation,
  onRenameConversation,
  onNewChat,
  conversations,
  activeConversations = [],
  currentConversationId,
  refreshTrigger
}: ConversationHistoryPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [conversationsState, setConversationsState] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameConversationId, setRenameConversationId] = useState<string | null>(null);
  const [renameConversationTitle, setRenameConversationTitle] = useState<string>('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [totalConversations, setTotalConversations] = useState(0);
  
  // Collapsible archived section (collapsed by default)
  const [isArchivedExpanded, setIsArchivedExpanded] = useState(false);
  
  // Show more state for each section (limit to 5 initially)
  const [showMoreRecent, setShowMoreRecent] = useState(false);
  const [showMoreOlder, setShowMoreOlder] = useState(false);
  
  // Scroll area ref for lazy loading
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Load conversations from API with pagination
  const loadConversations = useCallback(async (page: number = 1, append: boolean = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      // Load active conversations (non-archived first)
      const activeResponse = await getConversations({ 
        page, 
        limit: 20,
        include_archived: false // Load active conversations first
      });
      
      // Load archived conversations separately (always load them)
      const archivedResponse = await getConversations({ 
        page: 1, 
        limit: 100, // Load all archived conversations
        include_archived: true,
        archived_only: true // Only get archived ones
      });
      
      if (activeResponse.success && archivedResponse.success) {
        // Merge active and archived conversations
        const allConversations = [
          ...activeResponse.conversations,
          ...archivedResponse.conversations
        ];
        
        if (append) {
          // When appending, merge with existing but avoid duplicates
          setConversationsState(prev => {
            const existingIds = new Set(prev.map(c => c.id));
            const newConversations = allConversations.filter(c => !existingIds.has(c.id));
            return [...prev, ...newConversations];
          });
        } else {
          setConversationsState(allConversations);
        }
        
        setHasMore(activeResponse.pagination.hasMore);
        setTotalConversations(activeResponse.pagination.total + archivedResponse.pagination.total);
        setCurrentPage(page);
        
        // Debug: Log archived conversations
        const archivedCount = archivedResponse.conversations.length;
        const totalCount = allConversations.length;
        console.log(`[ConversationHistory] Loaded ${totalCount} conversations (${archivedCount} archived)`);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      if (!append) {
        setConversationsState([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isOpen) {
      loadConversations(1, false);
    }
  }, [isOpen, refreshTrigger, loadConversations]);

  // Load more conversations (lazy loading)
  const handleLoadMore = useCallback(() => {
    if (!isLoadingMore && hasMore) {
      loadConversations(currentPage + 1, true);
    }
  }, [currentPage, hasMore, isLoadingMore, loadConversations]);

  // Clear all archived conversations
  const handleClearAllArchived = useCallback(async () => {
    // Get archived conversations from current state
    const archivedConversations = conversationsState.filter(c => c.archived);
    
    if (archivedConversations.length === 0) {
      return;
    }

    // Confirm deletion
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete ${archivedConversations.length} archived conversation(s)? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      // Delete all archived conversations
      const deletePromises = archivedConversations.map(conv => 
        deleteConversation(conv.id, true) // Hard delete
      );

      await Promise.all(deletePromises);

      // Remove from local state
      setConversationsState(prev => 
        prev.filter(conv => !conv.archived)
      );

      toast.success(`Deleted ${archivedConversations.length} archived conversation(s)`);
      
      // Refresh to ensure consistency
      loadConversations(1, false);
    } catch (error) {
      console.error('Failed to clear archived conversations:', error);
      toast.error('Failed to delete archived conversations', {
        description: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }, [conversationsState, loadConversations]);

  // Optimistically update conversations state when archive happens
  const handleArchiveWithOptimisticUpdate = useCallback(async (id: string) => {
    // Optimistically update local state - mark as archived
    setConversationsState(prev => 
      prev.map(conv => 
        conv.id === id 
          ? { ...conv, archived: true, archived_at: new Date().toISOString() }
          : conv
      )
    );
    
    // Call the actual archive handler
    await onArchiveConversation?.(id);
    
    // Reload conversations from API to ensure consistency
    // Use a small delay to ensure database has been updated
    setTimeout(() => {
      loadConversations(1, false);
    }, 100);
  }, [onArchiveConversation, loadConversations]);

  const handleRenameClick = useCallback((id: string, currentTitle: string) => {
    setRenameConversationId(id);
    setRenameConversationTitle(currentTitle);
    setRenameDialogOpen(true);
  }, []);

  const handleRenameComplete = useCallback((newTitle: string) => {
    if (renameConversationId) {
      // Optimistically update local state
      setConversationsState(prev =>
        prev.map(conv =>
          conv.id === renameConversationId
            ? { ...conv, title: newTitle }
            : conv
        )
      );
      
      // Call parent handler if provided
      if (onRenameConversation) {
        onRenameConversation(renameConversationId, newTitle);
      }
    }
    setRenameDialogOpen(false);
    setRenameConversationId(null);
  }, [renameConversationId, onRenameConversation]);

  // Use provided conversations or fallback to API data
  const displayConversations = conversations || conversationsState;

  // Filter conversations based on search
  const filteredConversations = displayConversations.filter(conv =>
    conv.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.preview?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter active conversations (exclude current conversation from active list if it's already shown)
  const openConversations = activeConversations.filter(conv => {
    // Only show if not archived and matches search (if search is active)
    if (searchQuery) {
      return conv.title.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  const organizedConversations = organizeConversations(filteredConversations);
  
  // Debug: Log organized conversations
  useEffect(() => {
    if (organizedConversations.archived.length > 0) {
      console.log('[ConversationHistory] Archived conversations:', organizedConversations.archived.length);
    }
  }, [organizedConversations.archived.length]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 bottom-0 w-96 bg-background border-l border-border z-50 animate-in slide-in-from-right duration-300">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-4 border-b border-border bg-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-foreground">Conversation History</h2>
                {onNewChat && (
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      onNewChat();
                      onClose();
                    }}
                    title="New Chat"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    new
                  </Button>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-background border-border text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto bg-background" ref={scrollAreaRef}>
            <div className="p-4 space-y-6">
              {/* Loading State */}
              {isLoading && conversationsState.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50 animate-pulse" />
                  <p className="text-sm">Loading conversations...</p>
                </div>
              )}

              {/* OPEN - Active conversations at the very top */}
              {!isLoading && openConversations.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-foreground mb-2 uppercase">Open</h3>
                  <div className="space-y-2">
                    {openConversations.map(conv => {
                      // Convert ActiveConversation to Conversation format for the item component
                      const conversationItem: Conversation = {
                        id: conv.id,
                        title: conv.title,
                        created_at: conv.created_at || new Date().toISOString(),
                        updated_at: conv.updated_at || new Date().toISOString(),
                        message_count: conv.message_count || 0,
                        archived: false
                      };
                      return (
                        <ConversationHistoryItem
                          key={conv.id}
                          conversation={conversationItem}
                          onSelect={onSelectConversation}
                          onArchive={handleArchiveWithOptimisticUpdate}
                          onRename={handleRenameClick}
                          isActive={conv.id === currentConversationId}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* RECENT - At the top */}
              {!isLoading && organizedConversations.recent.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-foreground mb-2 uppercase">Recent</h3>
                  <div className="space-y-2">
                    {(showMoreRecent 
                      ? organizedConversations.recent 
                      : organizedConversations.recent.slice(0, 5)
                    ).map(conv => (
                      <ConversationHistoryItem
                        key={conv.id}
                        conversation={conv}
                        onSelect={onSelectConversation}
                        onArchive={handleArchiveWithOptimisticUpdate}
                        onRename={handleRenameClick}
                      />
                    ))}
                  </div>
                  {organizedConversations.recent.length > 5 && (
                    <button
                      onClick={() => setShowMoreRecent(!showMoreRecent)}
                      className="text-xs text-muted-foreground hover:text-foreground mt-2 px-2 py-1 -mx-2 rounded hover:bg-muted/50 transition-colors"
                    >
                      {showMoreRecent ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}

              {/* OLDER */}
              {!isLoading && organizedConversations.older.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-foreground mb-2 uppercase">Older</h3>
                  <div className="space-y-2">
                    {(showMoreOlder 
                      ? organizedConversations.older 
                      : organizedConversations.older.slice(0, 5)
                    ).map(conv => (
                      <ConversationHistoryItem
                        key={conv.id}
                        conversation={conv}
                        onSelect={onSelectConversation}
                        onArchive={handleArchiveWithOptimisticUpdate}
                        onRename={handleRenameClick}
                      />
                    ))}
                  </div>
                  {organizedConversations.older.length > 5 && (
                    <button
                      onClick={() => setShowMoreOlder(!showMoreOlder)}
                      className="text-xs text-muted-foreground hover:text-foreground mt-2 px-2 py-1 -mx-2 rounded hover:bg-muted/50 transition-colors"
                    >
                      {showMoreOlder ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}

              {/* ARCHIVED - At the bottom, collapsible */}
              {!isLoading && organizedConversations.archived.length > 0 && (
                <div className="border-t border-border pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      onClick={() => setIsArchivedExpanded(!isArchivedExpanded)}
                      className="flex items-center gap-2 hover:bg-muted/50 rounded-md p-2 -m-2 transition-colors flex-1"
                    >
                      <h3 className="text-xs font-semibold text-foreground uppercase">
                        Archived ({organizedConversations.archived.length})
                      </h3>
                      {isArchivedExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                    {isArchivedExpanded && organizedConversations.archived.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleClearAllArchived}
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                        title="Delete all archived conversations"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Clear All
                      </Button>
                    )}
                  </div>
                  {isArchivedExpanded && (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {organizedConversations.archived.length > 0 ? (
                        organizedConversations.archived.map(conv => (
                          <ConversationHistoryItem
                            key={conv.id}
                            conversation={conv}
                            onSelect={onSelectConversation}
                            onArchive={handleArchiveWithOptimisticUpdate}
                            onRename={handleRenameClick}
                            isArchived={true}
                          />
                        ))
                      ) : (
                        <p className="text-xs text-muted-foreground py-4 text-center">No archived conversations</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Empty State */}
              {!isLoading && filteredConversations.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">
                    {searchQuery ? 'No conversations found' : 'No conversation history yet'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Rename Dialog */}
      {renameConversationId && (
        <ConversationRenameDialog
          open={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          conversationId={renameConversationId}
          currentTitle={renameConversationTitle}
          onRenamed={handleRenameComplete}
        />
      )}
    </>
  );
}

function ConversationHistoryItem({
  conversation,
  onSelect,
  onArchive,
  onRename,
  isArchived = false,
  isActive = false
}: {
  conversation: Conversation;
  onSelect: (id: string) => void;
  onArchive: (id: string) => void;
  onRename?: (id: string, title: string) => void;
  isArchived?: boolean;
  isActive?: boolean;
}) {
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsArchiving(true);
    try {
      await onArchive(conversation.id);
    } finally {
      // Don't reset isArchiving - let the refresh handle it
      // This prevents the button from flashing back
    }
  };

  const handleRename = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRename?.(conversation.id, conversation.title);
  };

  return (
    <div
      className={cn(
        "group p-3 rounded-lg border border-border bg-card",
        "hover:bg-muted hover:border-border",
        "cursor-pointer transition-colors",
        (isArchived || isArchiving) && "opacity-60",
        isActive && "bg-primary/5 border-primary/20"
      )}
      onClick={() => onSelect(conversation.id)}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h4 className="font-medium text-sm line-clamp-1 text-foreground flex-1">{conversation.title}</h4>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* Archive Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 hover:bg-muted"
            onClick={handleArchive}
            disabled={isArchiving}
            title={isArchived ? "Unarchive" : "Archive"}
          >
            <Archive className={cn("h-3 w-3", isArchiving && "animate-pulse")} />
          </Button>
          
          {/* Context Menu */}
          {onRename && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 hover:bg-muted"
                  title="More options"
                >
                  <MoreVertical className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}>
                <DropdownMenuItem onClick={handleRename}>
                  <Edit className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleArchive}
                  disabled={isArchiving}
                  className={isArchived ? undefined : QUIET_MENU_DESTRUCTIVE_ITEM_CLASS}
                >
                  <Archive className="mr-2 h-4 w-4" />
                  {isArchived ? "Unarchive" : "Archive"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      
      {conversation.preview && (
        <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
          {conversation.preview}
        </p>
      )}
      
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{conversation.message_count} messages</span>
        <span>•</span>
        <span>
          {isArchived && conversation.archived_at
            ? new Date(conversation.archived_at).toLocaleDateString()
            : new Date(conversation.updated_at).toLocaleDateString()}
        </span>
      </div>
    </div>
  );
}

